import { pickMilestoneTimingRule } from "@common/helpers/domains/accounting/scheduleMilestones";
import { v4 as uuid } from "uuid";
import {
    getAllocationPercentUnits,
    getRemainingAllocationPercent,
    MAX_SCHEDULE_MILESTONES,
    splitAllocationPercents,
} from "@common/constants/domains/accounting/schedules";
import {
    ScheduleTemplateMilestoneInput,
    hasMilestoneComponent,
} from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import {
    RecognitionAnchor,
    RecognitionMethod,
    ScheduleMilestoneAnchor,
    ScheduleMilestoneOffsetUnit,
} from "@common/types/domains/accounting/schedule";
import type {
    ScheduleComponentForm,
    ScheduleTemplateFormValues,
} from "./scheduleFormHelpers";

export interface ScheduleMilestoneForm {
    key: string;
    name: string;
    allocationPercent: number;
    anchor: ScheduleMilestoneAnchor;
    offsetCount: number | null;
    offsetUnit: ScheduleMilestoneOffsetUnit | null;
    specificDate: string | null;
}

export function makeScheduleMilestone(
    overrides?: Partial<Omit<ScheduleMilestoneForm, "key">>,
): ScheduleMilestoneForm {
    return {
        key: uuid(),
        name: "",
        allocationPercent: 100,
        anchor: ScheduleMilestoneAnchor.sourceDate,
        offsetCount: null,
        offsetUnit: null,
        specificDate: null,
        ...overrides,
    };
}

export function makeMilestoneScheduleComponent(): ScheduleComponentForm {
    return {
        key: uuid(),
        method: RecognitionMethod.milestone,
        anchor: RecognitionAnchor.termStart,
        offsetDays: 0,
        allocationPercent: 100,
    };
}

export function switchScheduleComponentMethod(options: {
    values: Pick<ScheduleTemplateFormValues, "components" | "milestones">;
    index: number;
    method: RecognitionMethod;
}): Pick<ScheduleTemplateFormValues, "components" | "milestones"> {
    const { values, index, method } = options;
    if (method === RecognitionMethod.milestone) {
        return {
            components: values.components.map((component, componentIndex) =>
                componentIndex === index
                    ? {
                          ...makeMilestoneScheduleComponent(),
                          key: component.key,
                      }
                    : component,
            ),
            milestones:
                values.milestones.length === 0
                    ? [makeScheduleMilestone()]
                    : values.milestones,
        };
    }
    const isLeavingMilestone =
        values.components[index]?.method === RecognitionMethod.milestone;
    return {
        components: values.components.map((component, componentIndex) =>
            componentIndex === index ? { ...component, method } : component,
        ),
        milestones: isLeavingMilestone ? [] : values.milestones,
    };
}

export function toMilestoneBody(
    milestone: ScheduleTemplateMilestoneInput,
): ScheduleTemplateMilestoneInput {
    return {
        name: milestone.name,
        allocationPercent: milestone.allocationPercent,
        ...pickMilestoneTimingRule(milestone),
    };
}

export type ScheduleMilestoneTimingPatch = Pick<
    ScheduleMilestoneForm,
    "offsetCount" | "offsetUnit" | "anchor" | "specificDate"
>;

/**
 * "On" clears the offset and keeps the anchor; "After" counts from the
 * source date, keeping the row's offset or starting at one month.
 */
export function toMilestoneOffsetPatch(options: {
    milestone: Pick<ScheduleMilestoneForm, "offsetCount" | "offsetUnit">;
    hasOffset: boolean;
}): Partial<ScheduleMilestoneTimingPatch> {
    if (!options.hasOffset) {
        return { offsetCount: null, offsetUnit: null };
    }
    return {
        offsetCount: options.milestone.offsetCount ?? 1,
        offsetUnit:
            options.milestone.offsetUnit ?? ScheduleMilestoneOffsetUnit.months,
        anchor: ScheduleMilestoneAnchor.sourceDate,
        specificDate: null,
    };
}

/**
 * A specific date only works with "On", so picking it drops the offset;
 * the source date keeps the row's timing and clears the date.
 */
export function toMilestoneAnchorPatch(options: {
    milestone: Pick<ScheduleMilestoneForm, "specificDate">;
    anchor: ScheduleMilestoneAnchor;
}): Partial<ScheduleMilestoneTimingPatch> {
    if (options.anchor === ScheduleMilestoneAnchor.specificDate) {
        return {
            anchor: ScheduleMilestoneAnchor.specificDate,
            offsetCount: null,
            offsetUnit: null,
            specificDate: options.milestone.specificDate,
        };
    }
    return { anchor: ScheduleMilestoneAnchor.sourceDate, specificDate: null };
}

/** An applied milestone keeps its resolved day, so a preview of it is a rule on that specific date. */
export function toSpecificDateMilestoneBody(milestone: {
    name: string;
    allocationPercent: number;
    expectedDate: string;
}): ScheduleTemplateMilestoneInput {
    return {
        name: milestone.name,
        allocationPercent: milestone.allocationPercent,
        anchor: ScheduleMilestoneAnchor.specificDate,
        offsetCount: null,
        offsetUnit: null,
        specificDate: milestone.expectedDate,
    };
}

export function toMilestoneBodies(values: {
    components: ReadonlyArray<Pick<ScheduleComponentForm, "method">>;
    milestones: readonly ScheduleTemplateMilestoneInput[];
}): ScheduleTemplateMilestoneInput[] {
    if (!hasMilestoneComponent(values.components)) {
        return [];
    }
    return values.milestones.map(toMilestoneBody);
}

export function addScheduleMilestone(
    milestones: ScheduleMilestoneForm[],
): ScheduleMilestoneForm[] {
    if (milestones.length >= MAX_SCHEDULE_MILESTONES) {
        return milestones;
    }
    const remainingPercent = getRemainingAllocationPercent(
        milestones.map((milestone) => milestone.allocationPercent),
    );
    if (remainingPercent !== 0) {
        return [
            ...milestones,
            makeScheduleMilestone({
                allocationPercent: Math.max(remainingPercent, 0),
            }),
        ];
    }
    const evenPercents = splitAllocationPercents(milestones.length + 1);
    return [...milestones, makeScheduleMilestone()].map((milestone, index) => ({
        ...milestone,
        allocationPercent: evenPercents[index],
    }));
}

/**
 * The last milestone takes whatever the earlier rows leave, the way the last
 * recognition row does, so the plan always sums to 100 while it is edited.
 */
function withRemainderOnLastMilestone(
    milestones: ScheduleMilestoneForm[],
): ScheduleMilestoneForm[] {
    if (milestones.length === 0) {
        return milestones;
    }
    const lastIndex = milestones.length - 1;
    const lastPercent = Math.max(
        getRemainingAllocationPercent(
            milestones
                .slice(0, lastIndex)
                .map((milestone) => milestone.allocationPercent),
        ),
        0,
    );
    const last = milestones[lastIndex];
    if (
        getAllocationPercentUnits([last.allocationPercent]) ===
        getAllocationPercentUnits([lastPercent])
    ) {
        return milestones;
    }
    return milestones.map((milestone, index) =>
        index === lastIndex
            ? { ...milestone, allocationPercent: lastPercent }
            : milestone,
    );
}

/** The last row is the remainder, so only an earlier row's percent can change. */
export function isMilestoneAllocationLocked(options: {
    index: number;
    count: number;
}): boolean {
    return options.count <= 1 || options.index === options.count - 1;
}

export function setScheduleMilestoneAllocation(options: {
    milestones: ScheduleMilestoneForm[];
    index: number;
    allocationPercent: number;
}): ScheduleMilestoneForm[] {
    const { milestones, index, allocationPercent } = options;
    if (isMilestoneAllocationLocked({ index, count: milestones.length })) {
        return milestones;
    }
    return withRemainderOnLastMilestone(
        milestones.map((milestone, milestoneIndex) =>
            milestoneIndex === index
                ? { ...milestone, allocationPercent }
                : milestone,
        ),
    );
}

export function removeScheduleMilestone(
    milestones: ScheduleMilestoneForm[],
    index: number,
): ScheduleMilestoneForm[] {
    if (milestones.length <= 1) {
        return milestones;
    }
    return withRemainderOnLastMilestone(
        milestones.filter((_, milestoneIndex) => milestoneIndex !== index),
    );
}
